import React, { useEffect } from 'react'
import T from 'prop-types'
import { useLocation } from 'react-router-dom'
import { get } from 'lodash'
import { parse } from 'qs'
import useSWR from 'swr'
import Container from 'components/Container'
import Editorial from 'components/Editorial'
import Details from 'components/Details'
import Button from 'components/Button'
import { getSlot } from 'lib/requests'
import { uppercaseFirst } from 'lib/utils'
import { formatDateClean } from 'lib/date'
import { BOOKING_TYPES } from 'data/booking'
import useSession from 'hooks/useSession'
import { removeItem } from 'lib/storage'
import Header from 'apps/booking/components/Header'

import ConfirmationHeader from '../components/ConfirmationHeader'

// eslint-disable-next-line complexity
export default function ConfirmationPage({
  match: {
    params: { orgUnit },
  },
}) {
  const { clearSession } = useSession()
  const location = useLocation()

  const {
    checkGuidelines,
    selfDeclarationUpdate,
    s: slotId,
    n: name,
    type,
  } = parse(location.search, {
    ignoreQueryPrefix: true,
  })

  const { loading, data: slotData } = useSWR(!selfDeclarationUpdate ? [slotId] : null, getSlot)

  // Flush the session storage to prevent user from going back and getting confused
  useEffect(() => {
    removeItem('bookingState')
  }, [])

  if (!selfDeclarationUpdate && (loading || !slotData)) {
    return null
  }

  if (!selfDeclarationUpdate && (!slotId || !name)) {
    return (
      <>
        <Header />
        <Container size="tight">
          <h1>Det skjedde en feil</h1>
          <p>
            Vi klarer ikke vise deg en bekreftelse for timebestilling da det har skjedd en feil med
            lenken. Om du har fått en bekreftelse på SMS om din timebestilling skal alt være i
            orden, og vi beklager problemet. Hvis ikke, vennligst prøv igjen.
          </p>
          <Button arrow to={`/${orgUnit}`}>
            Bestill ny time
          </Button>
        </Container>
      </>
    )
  }

  const ordered = get(BOOKING_TYPES, `${type}.title`, '').toLowerCase()
  const title = selfDeclarationUpdate
    ? 'Egenerklæringen ble oppdatert'
    : `Du har bestilt ${ordered}`

  const checkGuidelinesParagraph = checkGuidelines ? (
    <p>
      Basert på svarene i egenerklæringen kan det være du bør ta spesielle forholdsregler i
      tilknytning til vaksinasjonen.{' '}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://www.fhi.no/contentassets/76e833e2ed2f4314ba7fabe0aba40b2e/vedlegg/sjekkliste/2021-02-12-sjekkliste-til-deg-som-er-tilbudt-koronavaksine.pdf"
      >
        Les mer
      </a>{' '}
      på FHI.no om dette.
    </p>
  ) : null

  return (
    <>
      <Header seoTitle={title} seoDescription={get(slotData, 'location.title')} />

      <Container size="wizard" style={{ paddingBottom: '3rem' }}>
        <ConfirmationHeader title={title} />
        {!selfDeclarationUpdate ? (
          <Details
            type="box"
            data={[
              {
                id: 'time',
                icon: 'time',
                value: uppercaseFirst(
                  formatDateClean(new Date(get(slotData, 'start_time')), 'EEEE d. MMMM HH:mm'),
                ),
              },
              {
                id: 'place',
                icon: 'place',
                value: get(slotData, 'location.title'),
              },
              {
                id: 'who',
                icon: 'user',
                value: name,
              },
            ]}
          />
        ) : null}

        {type === 'COVID_TEST' && (
          <>
            <Editorial>
              <p>
                Vi ber om at du møter opp til angitt sted og tid. Vi har dessverre ikke mulighet til
                å endre klokkeslett og/eller sted for din koronatest grunnet meget stor pågang og
                begrenset testkapasitet.
              </p>
              <p>
                Husk at du må forholde deg til{' '}
                <a href="https://www.fhi.no/nettpub/coronavirus/fakta/avstand-karantene-og-isolering/">
                  gjeldende karanteneregler
                </a>
                . Dersom du skulle bli dårligere kontakt din fastlege eller legevakten på telefon
                116 117.
              </p>
              <ul>
                <li>Ta med ID.</li>
                <li>Møt opp presis, men ikke før timen.</li>
                <li>Ta med papir/lommetørkle.</li>
                <li>Hold 1 meters avstand til andre.</li>
              </ul>
              {orgUnit !== 'vindafjord' && (
                <p>
                  Prøvesvar kommer elektronisk på{' '}
                  <a href="https://www.helsenorge.no/">helsenorge.no</a>. OBS: Du får ikke varsel
                  når prøvesvaret er klart, du må selv sjekke på din side på helsenorge.no.
                </p>
              )}
              {orgUnit === 'vindafjord' && (
                <>
                  <p>Det tar normalt sett 24 – 48 timer til prøvesvar er klart.</p>

                  <p>
                    Prøvesvar kommer elektronisk på{' '}
                    <a href="https://www.helsenorge.no/">helsenorge.no</a> eller{' '}
                    <a href="https://www.furstpasient.no/">furstpasient.no</a>. OBS: Du får ikke
                    varsel når prøvesvaret er klart, du må selv sjekke ved å logge inn.
                  </p>
                  <p>
                    Prøvesvar for Barn under 12 år er tilgjengelig for foreldre under samme
                    innlogging. For barn mellom 12 og 16 år må man bruke utlevert person-ID for
                    innlogging.
                  </p>
                  <p>
                    Det tas i tillegg utvida test for vanlig luftveis virus/ bakterier. Denne får du
                    SMS-svar på når prøvene er ferdig analysert.
                  </p>
                  <p>
                    NB: Du er i karantene frem til mottatt negativ prøvesvar.{' '}
                    <a href="https://www.fhi.no/nettpub/coronavirus/fakta/avstand-karantene-og-isolering/">
                      Les mer om karantene hos FHI
                    </a>
                    .
                  </p>
                </>
              )}
            </Editorial>
            <Button onClick={() => clearSession(`/${orgUnit}`)} primary>
              Avslutt og logg ut
            </Button>
          </>
        )}

        {type === 'COVID_VACCINE' && !selfDeclarationUpdate && (
          <>
            <Editorial>
              <p>Vi ber om at du møter opp til angitt sted og tid.</p>
              {checkGuidelinesParagraph}
              <p>
                Husk at du må forholde deg til{' '}
                <a href="https://www.fhi.no/nettpub/coronavirus/fakta/avstand-karantene-og-isolering/">
                  gjeldende karanteneregler
                </a>
                . Dersom du skulle bli dårligere kontakt din fastlege eller legevakten på telefon
                116 117.
              </p>
              <ul>
                <li>Ta med ID.</li>
                <li>Møt opp presis, men ikke før timen.</li>
                <li>Hold 1 meters avstand til andre.</li>
                <li>Husk at du må vente på vaksinasjonsstedet 20 minutter etter vaksinasjonen.</li>
              </ul>

              <p>
                <strong>For barn og unge under 16 år:</strong>
                <br />
                Samtykkeskjema må fylles ut og tas med til vaksinering. Vær oppmerksom på at ved
                felles foreldreansvar så må begge samtykke.
                <br />
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.fhi.no/publ/skjema/samtykkeskjema---vaksinasjon-av-barn-unge-under-16-ar/"
                >
                  Samtykkeskjema fra FHI
                </a>{' '}
                (åpnes i ny fane)
              </p>
            </Editorial>
            <Button onClick={() => clearSession(`/${orgUnit}`)} primary>
              Avslutt og logg ut
            </Button>
          </>
        )}

        {type === 'COVID_VACCINE' && selfDeclarationUpdate && (
          <>
            <Editorial>
              <p>Takk for at du oppdaterte din egenerklæring.</p>
              {checkGuidelinesParagraph}
              <p>
                Når dagen for din vaksinasjon kommer, husk at du må forholde deg til{' '}
                <a href="https://www.fhi.no/nettpub/coronavirus/fakta/avstand-karantene-og-isolering/">
                  gjeldende karanteneregler
                </a>
                .
              </p>
              <ul>
                <li>Ta med ID.</li>
                <li>Møt opp presis, men ikke før timen.</li>
                <li>Hold 1 meters avstand til andre.</li>
                <li>Husk at du må vente på vaksinasjonsstedet 20 minutter etter vaksinasjonen.</li>
              </ul>
            </Editorial>
            <Button onClick={() => clearSession(`/${orgUnit}`)} primary>
              Avslutt og logg ut
            </Button>
          </>
        )}

        {type === 'FLU_VACCINE' && (
          <>
            <Editorial>
              <p>
                Vi ber om at du møter opp til angitt sted og tid. Vi har dessverre ikke mulighet til
                å endre klokkeslett og/eller sted for din influensavaksine grunnet meget stor pågang
                og begrenset kapasitet.
              </p>
              <ul>
                <li>Ta med ID.</li>
                <li>Møt opp presis, men ikke før timen.</li>
                <li>Hold 1 meters avstand til andre.</li>
              </ul>
            </Editorial>
            <Button onClick={() => clearSession(`/${orgUnit}`)} primary>
              Avslutt og logg ut
            </Button>
          </>
        )}
      </Container>
    </>
  )
}

ConfirmationPage.propTypes = {
  match: T.shape({
    params: T.shape({
      orgUnit: T.string.isRequired,
    }).isRequired,
  }).isRequired,
}
